import { useState } from 'react';
import { ShieldCheck, ChevronDown, ChevronUp } from 'lucide-react';

// The one place the card hold is explained to customers.
//
// Checkout authorizes the card and only captures when the label is bought, so
// the shopper sees a pending amount on their statement that is not a charge.
// Two questions follow, in this order, every time:
//   1. why are you holding my money instead of taking it?
//   2. if you haven't charged me, can someone else buy this before I'm done?
//
// The second one is the anxious one and it is the reason the reserved line is
// not hidden behind the expander — it answers a fear, so it has to be visible
// without a click.
//
// Kept as one component rather than repeated copy on the cart, the checkout
// page and the order page, because three drifting explanations of the same
// mechanic is how a customer ends up unsure whether they have been charged.

export default function HoldNotice({ amountLabel, compact = false }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: compact ? '12px 14px' : '16px 18px',
        background: 'var(--surface)',
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <ShieldCheck size={18} style={{ flexShrink: 0, marginTop: 1, color: 'var(--success)' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: compact ? '0.88rem' : '0.95rem' }}>
            {amountLabel ? `${amountLabel} is held, not charged` : 'Your card is held, not charged'}
          </div>
          <div style={{ fontSize: '0.85rem', lineHeight: 1.55, color: 'var(--text-muted)', marginTop: 4 }}>
            We take payment when we print your shipping label. Your items are reserved
            for you the moment you order, so nobody else can buy them.
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              marginTop: 10,
              padding: 0,
              border: 'none',
              background: 'none',
              color: 'var(--accent)',
              fontSize: '0.82rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
            aria-expanded={open}
          >
            Why aren&rsquo;t you charging me now?
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {open && (
            <div style={{ fontSize: '0.85rem', lineHeight: 1.6, color: 'var(--text-muted)', marginTop: 10 }}>
              <p style={{ margin: '0 0 10px' }}>
                When you check out, your bank sets the money aside for us instead of sending it.
                You&rsquo;ll usually see it as a <strong>pending</strong> amount. It is not a payment,
                and it is how most careful shops handle orders that ship by hand.
              </p>
              <p style={{ margin: '0 0 10px' }}>
                <strong>It protects you.</strong> If something is wrong with your order, or we
                can&rsquo;t ship it for any reason, we release the hold and the money is simply
                never taken. There is no refund to wait on and no charge to dispute.
              </p>
              <p style={{ margin: '0 0 10px' }}>
                <strong>Your items are already yours.</strong> The second you place the order,
                everything in it comes off the store. Another customer can&rsquo;t buy it out from
                under you while your order is being packed, so there&rsquo;s nothing to race.
              </p>
              <p style={{ margin: 0 }}>
                We take the payment when we buy your shipping label, and you&rsquo;ll get an email
                with your tracking number the moment we do.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
